import { useQueryClient } from '@tanstack/react-query'
import { getGlobalToast } from '@/components/layout/toastUtils'
import { Button, Modal, useModalTitleId } from '@/components/ui'
import { invalidateAll } from '@/hooks/invalidation'
import { CloseIcon } from '@/icons'
import { useConflictStore } from '@/store/conflictStore'

// Rebuild a conflicted mutation's variables so re-executing actually overwrites
// the server instead of 409-ing again forever. The 409 was caused by a stale
// `expectedUpdatedAt` baked into these variables; the backend returns the fresh
// row in `serverData.current` (camelized at the client boundary, so its field
// is `updatedAt`), so we splice that back in (and mint a new idempotency key,
// since this is a distinct logical write). If no fresh timestamp is available we
// drop the guard entirely — the user explicitly chose to overwrite, so an
// unconditional write is correct and never loops. Both mutation variables and
// serverData are camelCase end-to-end (see `lib/caseTransform.ts`).
function rebuildOverwriteVariables(variables: unknown, serverData: unknown): unknown {
  if (!variables || typeof variables !== 'object') return variables

  const freshUpdatedAt = (serverData as { current?: { updatedAt?: string } } | null)?.current?.updatedAt
  const v = variables as Record<string, unknown>
  const next: Record<string, unknown> = { ...v, idempotencyKey: crypto.randomUUID() }

  // `expectedUpdatedAt` lives either nested under `data` (collection/sealed
  // updates) or at the top level. Refresh wherever it is.
  const applyTo = (obj: Record<string, unknown>) => {
    if (freshUpdatedAt) obj.expectedUpdatedAt = freshUpdatedAt
    else delete obj.expectedUpdatedAt
  }

  if (v.data && typeof v.data === 'object' && 'expectedUpdatedAt' in (v.data as object)) {
    const data = { ...(v.data as Record<string, unknown>) }
    applyTo(data)
    next.data = data
  } else if ('expectedUpdatedAt' in v) {
    applyTo(next)
  }
  return next
}

export default function ConflictResolutionModal() {
  // Select narrowly (store/CLAUDE.md) so unrelated conflict-store writes don't
  // re-render every consumer.
  const conflicts = useConflictStore((s) => s.conflicts)
  const removeConflict = useConflictStore((s) => s.removeConflict)
  const queryClient = useQueryClient()

  const conflict = conflicts[0]
  if (!conflict) return null

  const handleKeepServer = () => {
    // Just invalidate queries to refetch server state
    invalidateAll(queryClient)
    removeConflict(conflict.id)
  }

  const handleKeepMine = () => {
    const mutation = queryClient
      .getMutationCache()
      .getAll()
      .find(
        (m) =>
          m.options.mutationKey &&
          JSON.stringify(m.options.mutationKey) === JSON.stringify(conflict.mutationKey) &&
          JSON.stringify(m.state.variables) === JSON.stringify(conflict.variables),
      )

    if (!mutation) {
      // The mutation was GC'd or already retried — we can't re-apply it. Don't
      // pretend it succeeded: tell the user and pull fresh server state.
      getGlobalToast()?.('Could not re-apply your change — refreshing with the latest data.', 'error')
      invalidateAll(queryClient)
      removeConflict(conflict.id)
      return
    }

    // Re-execute with refreshed expectedUpdatedAt + a new idempotency key so
    // the write overwrites the server instead of conflicting again.
    mutation.execute(rebuildOverwriteVariables(conflict.variables, conflict.serverData))
    removeConflict(conflict.id)
  }

  const handleDiscard = () => {
    removeConflict(conflict.id)
  }

  return (
    <Modal
      isOpen
      onClose={handleDiscard}
      className="bg-surface border border-theme-border rounded-lg shadow-xl max-w-md w-full"
    >
      <ConflictBody
        errorMessage={conflict.errorMessage}
        remainingCount={conflicts.length - 1}
        onKeepMine={handleKeepMine}
        onKeepServer={handleKeepServer}
        onDiscard={handleDiscard}
      />
    </Modal>
  )
}

interface ConflictBodyProps {
  errorMessage: string
  remainingCount: number
  onKeepMine: () => void
  onKeepServer: () => void
  onDiscard: () => void
}

/**
 * Rendered inside <Modal> so it can read the Modal's title id from context
 * and wire it up to the header heading, giving the dialog an accessible name
 * via the existing aria-labelledby on the dialog element.
 */
function ConflictBody({ errorMessage, remainingCount, onKeepMine, onKeepServer, onDiscard }: ConflictBodyProps) {
  const titleId = useModalTitleId()

  return (
    <>
      <div className="flex items-center justify-between px-4 py-3 border-b border-theme-border">
        <h2 id={titleId} className="text-base font-medium text-yellow-400">
          Conflict Detected
        </h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={onDiscard}
          className="p-1"
          aria-label="Close"
          icon={<CloseIcon className="w-4 h-4" />}
        />
      </div>
      <div className="px-4 py-4">
        <p className="text-sm text-secondary mb-4">
          This item was modified by another session. Your change conflicts with the current server state.
        </p>
        <p className="text-xs text-muted mb-4">{errorMessage}</p>
        <div className="flex flex-col gap-2">
          <Button onClick={onKeepMine} className="w-full">
            Keep my change (overwrite server)
          </Button>
          <Button variant="secondary" onClick={onKeepServer} className="w-full">
            Keep server version (discard my change)
          </Button>
          <Button variant="ghost" onClick={onDiscard} className="w-full">
            Dismiss
          </Button>
        </div>
        {remainingCount > 0 && (
          <p className="text-xs text-faint mt-3 text-center">
            {remainingCount} more conflict{remainingCount > 1 ? 's' : ''} pending
          </p>
        )}
      </div>
    </>
  )
}
